<template>
    <div class="card">
        <div class="relative card-body flex flex-col">
            <button
                v-if="canDelete"
                @click="deleteThis"
                type="button"
                class="btn btn-danger btn-sm btn-delete"
                v-html="$t('configurator.remove')"
            ></button>
            <form>
                <div class="form-group flagEmojiPolyfill">
                    <label for="country" v-html="$t('configurator.select_country')"></label>
                    <select
                        class="form-control"
                        :class="{ 'is-invalid': formErrors.systemKey }"
                        id="country"
                        v-model="userData.systemKey"
                        :disabled="!!systemKey"
                    >
                        <option v-text="$t('productConfigurator.selectPlaceholder')" value="" disabled />
                        <option v-for="system in systems" :key="system.systemKey" :value="system.systemKey">
                            {{ getCountryFlagEmoji(system.isoCode) }} {{ system.localizedName }}
                        </option>
                    </select>
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.systemKey" :key="error">
                            {{ error }}
                        </span>
                    </div>
                </div>
                <div class="form-group">
                    <a
                        class="ml-2 cursor-pointer"
                        @click.prevent="openVehicleInformationModal"
                        v-if="vehicleInformation.length > 0"
                    >
                        <i class="mdi mdi-information-outline icon"></i>
                    </a>
                    <label for="vehicleType" v-html="$t('configurator.select_vehicle_type')"></label>
                    <select
                        class="form-control"
                        :class="{ 'is-invalid': formErrors.vehicleType }"
                        id="vehicleType"
                        v-model="userData.vehicleType"
                    >
                        <option
                            v-for="vehicleType in vehicleTypes"
                            :key="vehicleType.id"
                            :value="vehicleType.customizedVehicleName"
                            v-html="vehicleType.labels[$language]"
                        ></option>
                    </select>
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.vehicleType" :key="error">
                            {{ error }}
                        </span>
                    </div>
                </div>
                <div class="form-group">
                    <label for="startDate" v-html="$t('configurator.select_start_date')"></label>
                    <input
                        type="date"
                        :min="new Date().toJSON().slice(0, 10)"
                        :max="maxValidityStart"
                        class="form-control date-selection"
                        :class="{ 'is-invalid': formErrors.startDate }"
                        id="startDate"
                        v-model="userData.startDate"
                        onfocus="showPicker()"
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.startDate" :key="error">
                            {{ error }}
                        </span>
                    </div>
                </div>
                <div class="form-group">
                    <label for="endDate" v-html="$t('configurator.select_end_date')"></label>
                    <input
                        type="date"
                        :min="new Date().toJSON().slice(0, 10)"
                        :max="maxValidityEnd"
                        class="form-control date-selection"
                        :class="{ 'is-invalid': formErrors.endDate }"
                        id="endDate"
                        v-model="userData.endDate"
                        onfocus="showPicker()"
                    />
                    <div class="invalid-feedback">
                        <span class="d-block" v-for="error in formErrors.endDate" :key="error">
                            {{ error }}
                        </span>
                    </div>
                </div>
            </form>
        </div>

        <VehicleInformationModal
            v-if="vehicleInformation.length > 0"
            ref="vehicleInformationModal"
            :vehicle-information="vehicleInformation"
        />
    </div>
</template>

<script>
import VehicleInformationModal from '../../Shop/VehicleInformationModal.vue';
import CountryFlagEmoji from '../../../mixins/CountryFlagEmoji';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';

export default {
    name: 'CalendarEntry',
    components: { VehicleInformationModal },
    mixins: [CountryFlagEmoji],
    inject: ['systems'],

    props: {
        id: {
            type: String,
            required: true,
        },
        systemKey: {
            type: String,
            required: false,
        },
        canDelete: {
            type: Boolean,
            default: false,
        },
        maxValidityStart: {
            type: String,
            required: false,
        },
        maxValidityEnd: {
            type: String,
            required: false,
        },
        preselectedUserData: {
            type: Object,
            required: false,
        },
    },

    data() {
        return {
            formErrors: {},
            userData: {
                startDate: this.preselectedUserData.startDate ?? '',
                endDate: this.preselectedUserData.endDate ?? '',
                systemKey: this.preselectedUserData.systemKey ?? '',
                vehicleType: this.preselectedUserData.vehicleType ?? '',
            },
        };
    },

    computed: {
        system() {
            return this.systems.find((system) => system.systemKey === this.userData.systemKey);
        },
        vehicleTypes() {
            return this.getProductVehicleOptions(this.system?.products ?? []);
        },
        vehicleInformation() {
            if (!this.system || !Array.isArray(this.system.products)) {
                return [];
            }

            const productOptions = this.getProductVehicleOptions(this.system.products);
            const vehicleInformation = new Set();

            productOptions.forEach((opt) => {
                if (opt.vehicleDescriptions && typeof opt.vehicleDescriptions === 'object') {
                    const description = opt.vehicleDescriptions[this.$language] ?? opt.vehicleDescriptions['en'];
                    if (description) {
                        vehicleInformation.add(description.trim());
                    }
                }
            });

            return Array.from(vehicleInformation);
        },
    },

    watch: {
        userData: {
            handler: function (val) {
                this.$emit('user-data-updated', this.id, val);
            },
            deep: true,
        },
        ['userData.systemKey']() {
            this.userData.vehicleType = this.vehicleTypes[0].customizedVehicleName;
        },
    },

    methods: {
        deleteThis() {
            this.$emit('delete', this.id);
        },
        validate() {
            let hasErrors = false;
            this.formErrors = {};

            for (const key in this.userData) {
                if (
                    !this.userData[key] ||
                    (typeof this.userData[key] === 'string' && this.userData[key].length === 0)
                ) {
                    this.formErrors[key] ??= [];
                    this.formErrors[key].push(
                        this.$t('configurator.validation_value_needed', {
                            field: this.$t('configurator.validation_field_' + key),
                        })
                    );
                    hasErrors = true;
                }
            }

            const startDate = new Date(this.userData.startDate);
            const endDate = new Date(this.userData.endDate);
            if (endDate < startDate) {
                this.formErrors['endDate'] ??= [];
                this.formErrors['endDate'].push(this.$t('configurator.validation_endDate_lt_startDate'));
                hasErrors = true;
            }

            if (this.maxValidityStart) {
                const maxValidityStart = new Date(this.maxValidityStart);
                if (startDate > maxValidityStart) {
                    this.formErrors['startDate'] ??= [];
                    this.formErrors['startDate'].push(
                        this.$t('configurator.validation_date_lte_max', {
                            field: this.$t('configurator.validation_field_startDate'),
                            date: maxValidityStart.toLocaleDateString(),
                        })
                    );
                    hasErrors = true;
                }
            }

            if (this.maxValidityEnd) {
                const maxValidityEnd = new Date(this.maxValidityEnd);
                if (endDate > maxValidityEnd) {
                    this.formErrors['endDate'] ??= [];
                    this.formErrors['endDate'].push(
                        this.$t('configurator.validation_date_lte_max', {
                            field: this.$t('configurator.validation_field_endDate'),
                            date: maxValidityEnd.toLocaleDateString(),
                        })
                    );
                    hasErrors = true;
                }
            }

            return !hasErrors;
        },
        getProductVehicleOptions(systemProducts) {
            let maxOptionsLength = -1;
            let maxProduct;

            systemProducts.forEach((product) => {
                const optionLength = Object.values(product.options).length;
                if (optionLength > maxOptionsLength) {
                    maxProduct = product;
                    maxOptionsLength = optionLength;
                }
            });
            return maxProduct?.options ?
                Object.values(maxProduct.options).toSorted((a, b) => a.sort - b.sort) :
                [];
        },
        openVehicleInformationModal() {
            this.$refs.vehicleInformationModal.showModal();
        },
    },

    mounted() {
        polyfillCountryFlagEmojis('Twemoji Country Flags', '/assets/fonts/TwemojiCountryFlags.woff2');

        if (this.systemKey) {
            this.userData.systemKey = this.systemKey;
        }
    },
};
</script>

<style scoped lang="scss">
.btn-delete {
    position: absolute;
    right: 1.25rem;
    top: 0.6rem;
}
.date-selection {
    appearance: none;
}

.flagEmojiPolyfill {
    font-family: "Twemoji Country Flags", Prompt, sans-serif;
}
</style>
